import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Users, ClipboardList, GitBranch, DollarSign, ArrowRight, Contact,
  MapPin, Clock, Layers, RotateCw
} from "lucide-react";
import { motion } from "framer-motion";

const LANDING_CSS = `
.landing-root { overflow-x: hidden; padding-left: env(safe-area-inset-left); padding-right: env(safe-area-inset-right); }
.landing-root button, .landing-root a { -webkit-tap-highlight-color: transparent; touch-action: manipulation; user-select: none; }
.landing-root button { min-height: 44px; }
.flip-scene { perspective: 1400px; }
.flip-inner { transform-style: preserve-3d; transition: transform 0.7s cubic-bezier(0.22, 1, 0.36, 1); }
.flip-scene:hover .flip-inner, .flip-scene:focus-visible .flip-inner, .flip-scene.is-flipped .flip-inner { transform: rotateY(180deg); }
.flip-face { backface-visibility: hidden; -webkit-backface-visibility: hidden; }
.flip-back { transform: rotateY(180deg); }
@media (hover: none) and (pointer: coarse) {
  .flip-scene:hover .flip-inner { transform: none; }
  .landing-root button:active { opacity: 0.9; }
}
@media (max-height: 520px) and (orientation: landscape) {
  .landing-hero { padding-top: 1.25rem; padding-bottom: 1.25rem; }
  .landing-stack { display: none; }
}
@media (prefers-reduced-motion: reduce) {
  .flip-inner { transition: none; }
}
`;

const heroCards = [
  {
    icon: Users,
    title: "Doctor Rolodex",
    desc: "Deduplicated doctor database at your fingertips",
    accent: "text-blue-600 dark:text-blue-400",
    z: 3, y: 0, x: 0, rotate: 0, scale: 1,
  },
  {
    icon: GitBranch,
    title: "10-Stage Pipeline",
    desc: "Full patient journey from referral to TT report",
    accent: "text-purple-600 dark:text-purple-400",
    z: 2, y: -26, x: 34, rotate: 3, scale: 0.96,
  },
  {
    icon: DollarSign,
    title: "Commission Tracking",
    desc: "Auto-calculated per medical aid and stage",
    accent: "text-amber-600 dark:text-amber-400",
    z: 1, y: -54, x: 68, rotate: 6, scale: 0.92,
  },
];

const features = [
  {
    icon: Users,
    title: "Doctor Rolodex",
    front: "Deduplicated database",
    back: "Search 200+ practices by city, area or suburb — duplicates merged automatically.",
    tone: "from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900",
    accent: "text-blue-600 dark:text-blue-400",
  },
  {
    icon: ClipboardList,
    title: "Visit Logging",
    front: "60-second entry",
    back: "Log a visit from the field in under a minute — doctor, outcome and follow-up in one card.",
    tone: "from-emerald-50 to-emerald-100 dark:from-emerald-950 dark:to-emerald-900",
    accent: "text-emerald-600 dark:text-emerald-400",
  },
  {
    icon: GitBranch,
    title: "Pipeline Tracking",
    front: "10 stages, real-time",
    back: "Drag a case between stages and every stage date is stamped for you.",
    tone: "from-purple-50 to-purple-100 dark:from-purple-950 dark:to-purple-900",
    accent: "text-purple-600 dark:text-purple-400",
  },
  {
    icon: DollarSign,
    title: "Commission Tracking",
    front: "Auto-calculated",
    back: "Eligibility resolves itself from the medical aid, plan and the stage a case reaches.",
    tone: "from-amber-50 to-amber-100 dark:from-amber-950 dark:to-amber-900",
    accent: "text-amber-600 dark:text-amber-400",
  },
];

const stats = [
  { icon: Layers, value: "10", label: "Pipeline stages" },
  { icon: Users, value: "4", label: "Reps on the team" },
  { icon: MapPin, value: "5", label: "Areas covered" },
  { icon: Clock, value: "60s", label: "Per visit entry" },
];

const FlipCard = ({ feature, index }) => {
  const [flipped, setFlipped] = useState(false);
  const Icon = feature.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: 0.15 + index * 0.08 }}
      className={`flip-scene ${flipped ? "is-flipped" : ""} h-[190px] sm:h-[200px] lg:h-[210px]`}
      data-testid={`feature-card-${index}`}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => setFlipped((v) => !v)}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setFlipped((v) => !v)}
        className="flip-inner relative w-full h-full cursor-pointer outline-none"
      >
        <div className={`flip-face absolute inset-0 rounded-xl border border-slate-200 dark:border-slate-700 bg-gradient-to-br ${feature.tone} p-5 sm:p-6 shadow-md`}>
          <Icon className={`w-7 h-7 sm:w-8 sm:h-8 ${feature.accent} mb-3`} />
          <h4 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white">{feature.title}</h4>
          <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 mt-1">{feature.front}</p>
          <span className="absolute bottom-4 left-5 sm:left-6 inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 dark:text-slate-400">
            <RotateCw className="w-3 h-3" /> Flip
          </span>
        </div>
        <div className="flip-face flip-back absolute inset-0 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 sm:p-6 shadow-lg flex flex-col justify-center">
          <h4 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white mb-2">{feature.title}</h4>
          <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{feature.back}</p>
        </div>
      </div>
    </motion.div>
  );
};

const LandingPage = () => {
  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  useEffect(() => {
    if (!document.getElementById("landing-theme")) {
      const el = document.createElement("style");
      el.id = "landing-theme";
      el.textContent = LANDING_CSS;
      document.head.appendChild(el);
    }
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute("content", "width=device-width, initial-scale=1, viewport-fit=cover");
    }
    const saved = localStorage.getItem("crm-theme-mode");
    if (saved) {
      document.documentElement.classList.toggle("dark", JSON.parse(saved));
    }
  }, []);

  return (
    <div
      className="landing-root min-h-screen min-h-[100dvh] flex flex-col bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900"
      style={{ fontFamily: "Manrope, sans-serif" }}
      data-testid="landing-page"
    >
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/60 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-14 sm:h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Contact className="w-5 h-5 shrink-0 text-blue-600 dark:text-blue-400" />
            <span className="text-sm sm:text-base font-bold text-slate-900 dark:text-white truncate">
              RolodexCRM
            </span>
          </div>
          <Button
            onClick={handleLogin}
            size="sm"
            data-testid="header-login-button"
            className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white rounded-lg px-5 min-h-11 whitespace-nowrap"
          >
            Sign In
          </Button>
        </div>
      </header>

      <main className="flex-1 w-full">
        {/* Hero */}
        <section className="landing-hero max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10 sm:py-14 lg:py-20">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
            <div>
              <motion.p
                initial={{ opacity: 0, x: -14 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.45 }}
                className="text-[11px] sm:text-xs font-semibold tracking-[0.2em] uppercase text-blue-600 dark:text-blue-400 mb-4"
              >
                Sleep apnea referral pipeline
              </motion.p>

              <motion.h1
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.05 }}
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 dark:text-white leading-[1.08]"
              >
                Your digital
                <span className="block text-blue-600 dark:text-blue-400">patient rolodex</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.12 }}
                className="text-sm sm:text-base lg:text-lg text-slate-600 dark:text-slate-300 mt-5 sm:mt-6 max-w-xl leading-relaxed"
              >
                Organise, track and manage every referral from intake to completion — doctors,
                visits, stages and commission in one place.
              </motion.p>

              {/* Sign-in card (auth logic unchanged) */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.2 }}
                className="mt-9 sm:mt-10 max-w-md mx-auto lg:mx-0"
              >
                <Card className="border-slate-200 bg-white dark:bg-slate-800 dark:border-slate-700 shadow-lg lg:shadow-xl">
                  <CardContent className="p-5 sm:p-7">
                    <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white">
                      Sign in to RolodexCRM
                    </h3>
                    <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1 mb-5 sm:mb-6">
                      Internal system for reps and management. Use your work Google account.
                    </p>
                    <Button
                      size="lg"
                      onClick={handleLogin}
                      data-testid="main-login-button"
                      className="w-full bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white rounded-lg text-sm sm:text-base min-h-12 py-5 sm:py-6 gap-2 group"
                    >
                      Enter CRM
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            {/* Rolodex card stack (3D offsets) */}
            <div className="landing-stack relative h-[300px] md:h-[340px] lg:h-[360px] hidden md:block" aria-hidden="true">
              {heroCards.map((card, i) => {
                const Icon = card.icon;
                return (
                  <motion.div
                    key={card.title}
                    initial={{ opacity: 0, y: card.y - 30, rotate: 0 }}
                    animate={{ opacity: 1, y: card.y, rotate: card.rotate }}
                    transition={{ duration: 0.6, delay: 0.2 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                    whileHover={{ y: card.y - 14, rotate: card.rotate * 0.35 }}
                    style={{ zIndex: card.z, x: card.x, scale: card.scale }}
                    className="absolute inset-x-0 top-16 mx-auto w-full max-w-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 lg:p-8 shadow-xl"
                  >
                    <Icon className={`w-8 h-8 ${card.accent} mb-4`} />
                    <h3 className="text-lg lg:text-xl font-bold text-slate-900 dark:text-white">
                      {card.title}
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">{card.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="bg-blue-50/70 dark:bg-slate-900/60 border-y border-slate-200 dark:border-slate-800 py-10 sm:py-12 lg:py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-5 lg:gap-6">
              {stats.map((stat, idx) => {
                const Icon = stat.icon;
                return (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.05 * idx }}
                    className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 sm:p-6 shadow-sm text-center"
                    data-testid={`stat-card-${idx}`}
                  >
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 dark:text-blue-400 mx-auto mb-2" />
                    <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 dark:text-white">
                      {stat.value}
                    </div>
                    <div className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 mt-0.5">
                      {stat.label}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Flippable feature cards */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20">
          <div className="text-center mb-8 sm:mb-10 lg:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 dark:text-white">
              Key features
            </h2>
            <p className="text-sm sm:text-base text-slate-500 dark:text-slate-400 mt-2">
              Hover or tap a card to flip it over
            </p>
          </div>

          <div className="grid grid-cols-1 min-[420px]:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
            {features.map((feature, idx) => (
              <FlipCard key={feature.title} feature={feature} index={idx} />
            ))}
          </div>
        </section>
      </main>

      <footer className="bg-slate-900 dark:bg-slate-950 text-white py-8 sm:py-10 pb-[max(2rem,env(safe-area-inset-bottom))]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 text-center">
          <p className="text-slate-300 text-xs sm:text-sm mb-1">Internal use only — RolodexCRM</p>
          <p className="text-slate-500 text-xs">Sleep Apnea Referral Pipeline Management</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
